import * as React from "react"
import { Button } from "gatsby-interface"
import { useLocalStorage } from "../../utils/use-local-storage"
import * as styles from "./early-access.css"
import format from "date-fns/format"
import clsx from "clsx"
import { useEarlyAccessRequestDataQuery } from "../settings/queries.generated"
import { Link } from "@gatsbyjs/reach-router"

// there must be corresponding base, defined in /api/feature-early-access, to store feature requests
export enum Feature {
  DataLayer = "DATA_LAYER",
}

export type EarlyAccessProps = {
  feature: Feature
  siteId: string
  workspaceId: string
  btnLabel?: string
  alreadyRequestedMsg?: React.ReactNode
  thankYouMsg?: React.ReactNode
  granted?: boolean
}

export function EarlyAccess({
  siteId,
  workspaceId,
  btnLabel = `Request Access`,
  thankYouMsg = `Thank you! Your request was successfully submitted!`,
  alreadyRequestedMsg = (
    <>
      Content Hub is in Early Access.
      <br /> Your request for access has been recorded.
    </>
  ),
  feature,
}: EarlyAccessProps) {
  const [error, setError] = React.useState(``)
  const [isSending, setIsSending] = React.useState(false)
  const [justRequested, setJustRequested] = React.useState(false)
  const [alreadyRequested, setAlreadyRequested] = useLocalStorage<string>(
    `gatsby:${feature}-early-access-request`,
    ""
  )

  const forRequest = useEarlyAccessRequestDataQuery({
    variables: {
      siteId,
      organizationId: workspaceId,
    },
    skip: Boolean(alreadyRequested) || justRequested,
  })

  const handleClick = async () => {
    setIsSending(true)

    const now = format(new Date(), "MMM d, yyyy hh:mm")

    const body = JSON.stringify({
      userName: forRequest.data?.currentUser?.name,
      userId: forRequest.data?.currentUser?.id,
      userEmail: forRequest.data?.currentUser?.email,
      siteId,
      siteName: forRequest.data?.siteDetails?.publicName,
      workspaceId,
      workspaceName: forRequest.data?.organizationDetails?.name,
      plan: forRequest.data?.organizationDetails?.billing?.plan?.baseFeatures
        ?.tier,
      date: now,
      feature,
    })

    const data = await fetch("/api/feature-early-access", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body,
    }).then(res => {
      return res.json()
    })

    if (data.success) {
      setJustRequested(true)
      setAlreadyRequested(now)
    } else {
      setIsSending(false)
      setError(data.error)
    }
  }

  if (error) {
    return (
      <div className={styles.root}>
        <p className={clsx(styles.message, styles.error)}>
          Something went wrong. Please try later or{" "}
          <Link to="/contact-us/" className={styles.supportLink}>
            Contact Support
          </Link>
        </p>
      </div>
    )
  }

  if (justRequested || alreadyRequested) {
    return (
      <div className={styles.root}>
        <p className={styles.message}>
          {justRequested ? thankYouMsg : alreadyRequestedMsg}
        </p>
      </div>
    )
  }

  return (
    <div className={styles.root}>
      <Button
        onClick={handleClick}
        size="L"
        loading={isSending}
        loadingLabel="Submitting..."
        disabled={isSending}
      >
        {btnLabel}
      </Button>
    </div>
  )
}
